import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import { getUsers, createUser, updateUser, deleteUser, bulkImportUsers } from '../services/api';

const UserManagement = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    isAdmin: false,
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getUsers();
      setUsers(data);
    } catch (err) {
      setError(err.message || 'Error fetching users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      password: '',
      isAdmin: false,
    });
    setEditUser(null);
    setShowAddForm(false);
    setShowEditForm(false);
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      await createUser(formData);
      await fetchUsers();
      resetForm();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleEditClick = (user) => {
    setEditUser(user);
    setFormData({
      name: user.name,
      username: user.username,
      password: '',
      isAdmin: user.isAdmin,
    });
    setShowEditForm(true);
    setShowAddForm(false);
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      const bodyData = { ...formData };
      if (!bodyData.password) {
        delete bodyData.password; // Do not update password if empty
      }
      await updateUser(editUser.id, bodyData);
      await fetchUsers();
      resetForm();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await deleteUser(id);
      await fetchUsers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const data = new Uint8Array(evt.target.result);
      const workbook = XLSX.read(data, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Map Excel data to user objects, default isAdmin to false if not specified
      const usersToImport = jsonData.map((row) => ({
        name: row.name || '',
        username: row.username || '',
        password: row.password || 'defaultPassword123', // or prompt user to change later
        isAdmin: row.isAdmin === true || row.isAdmin === 'true' ? true : false,
      }));

      importUsers(usersToImport);
    };
    reader.readAsArrayBuffer(file);
  };

  const importUsers = async (usersToImport) => {
    try {
      await bulkImportUsers(usersToImport);
      await fetchUsers();
      alert('Users imported successfully');
    } catch (err) {
      alert(err.message);
    }
  };

  if (!isAdmin) {
    return <div className="p-8">Access denied. Admins only.</div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-3xl font-bold text-gray-900">User Management</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200"
          >
            Add New User
          </button>
          <label
            htmlFor="import-users"
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 cursor-pointer"
          >
            Import Excel
          </label>
          <input
            type="file"
            id="import-users"
            accept=".xlsx, .xls"
            onChange={handleFileUpload}
            className="hidden"
          />
        </div>
      </div>

      {loading ? (
        <div>Loading users...</div>
      ) : error ? (
        <div className="text-red-600">Error: {error}</div>
      ) : (
        <>

          {(showAddForm || showEditForm) && (
            <form
              onSubmit={showAddForm ? handleAddUser : handleEditUser}
              className="mb-6 p-4 border rounded bg-white shadow"
            >
              <div className="mb-2">
                <label className="block mb-1 font-medium">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div className="mb-2">
                <label className="block mb-1 font-medium">Username</label>
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  required
                  className="w-full border px-3 py-2 rounded"
                  disabled={showEditForm}
                />
              </div>
              <div className="mb-2">
                <label className="block mb-1 font-medium">
                  Password {showEditForm ? '(leave blank to keep current)' : ''}
                </label>
                <input
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="w-full border px-3 py-2 rounded"
                />
              </div>
              <div className="mb-2 flex items-center">
                <input
                  type="checkbox"
                  name="isAdmin"
                  checked={formData.isAdmin}
                  onChange={handleInputChange}
                  id="isAdmin"
                />
                <label htmlFor="isAdmin" className="ml-2 font-medium">
                  Admin
                </label>
              </div>
              <div>
                <button
                  type="submit"
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700 mr-2"
                >
                  {showAddForm ? 'Add User' : 'Update User'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          )}

          <table className="min-w-full bg-white border">
            <thead>
              <tr>
                <th className="py-2 px-4 border-b">Name</th>
                <th className="py-2 px-4 border-b">Username</th>
                <th className="py-2 px-4 border-b">Admin</th>
                <th className="py-2 px-4 border-b">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="hover:bg-gray-100">
                  <td className="py-2 px-4 border-b">{u.name}</td>
                  <td className="py-2 px-4 border-b">{u.username}</td>
                  <td className="py-2 px-4 border-b text-center">
                    {u.isAdmin ? 'Yes' : 'No'}
                  </td>
                  <td className="py-2 px-4 border-b space-x-2 text-center">
                    <button
                      onClick={() => handleEditClick(u)}
                      title='Edit'
                      className="inline-flex items-center p-2 border border-gray-300 rounded-full shadow-sm bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u.id)}
                      className="inline-flex items-center p-2 border border-gray-300 rounded-full shadow-sm bg-white text-gray-400 hover:text-red-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      disabled={u.id === user.id}
                      title={u.id === user.id ? "You can't delete yourself" : 'Delete'}
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="4" className="text-center py-4">
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
};

export default UserManagement;

